import { useState, useRef } from 'react';
import { api } from '../lib/api.js';
import { ShortCodeTag } from './ShortCodeTag.js';

interface Company {
  id?: string;
  short_code: string;
  company_name?: string;
  occupier_name?: string;
  factory_address?: string;
  license_number?: string;
  registration_number?: string;
  contact_person?: string;
  mobile_number?: string;
  contact_email?: string;
  communication_email?: string;
}

interface Props {
  shortCode: string;
  company?: Company | null;
  companyId?: string;
  className?: string;
  children?: React.ReactNode;
}

export function CompanyHoverBadge({ shortCode, company: initialCompany, companyId, className, children }: Props) {
  const [hovered, setHovered] = useState(false);
  const [fetchedCompany, setFetchedCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const lastFetchedIdRef = useRef<string | null>(null);

  const company = initialCompany || fetchedCompany;

  const handleMouseEnter = async (e: React.MouseEvent) => {
    setPos({ x: e.clientX, y: e.clientY });
    setHovered(true);

    if (initialCompany && initialCompany.license_number && initialCompany.factory_address) return;

    if (companyId && lastFetchedIdRef.current !== companyId) {
      lastFetchedIdRef.current = companyId;
      setLoading(true);
      try {
        const res = await api.companies.get(companyId);
        if (res.data) setFetchedCompany(res.data as Company);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    setPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  return (
    <span
      className={`inline-block relative cursor-help ${className || ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children || <ShortCodeTag code={shortCode} />}

      {hovered && (
        <div
          className="fixed z-50 w-72 pointer-events-none p-3.5 bg-slate-900 text-white rounded-xl shadow-2xl border border-slate-700 space-y-2 text-xs animate-in fade-in zoom-in-95 duration-150"
          style={{
            left: `${Math.max(10, Math.min(pos.x - 140, window.innerWidth - 300))}px`,
            top: `${pos.y < 220 ? pos.y + 15 : Math.max(10, pos.y - 190)}px`,
          }}
        >
          <div className="flex items-center justify-between border-b border-slate-800 pb-2">
            <div className="flex items-center gap-1.5 font-bold text-brand-300">
              <span className="px-1.5 py-0.5 rounded bg-brand-500/20 text-brand-300 border border-brand-500/30 text-[10px] font-mono">
                {company?.short_code || shortCode}
              </span>
              <span className="truncate max-w-[170px] text-slate-100">{company?.company_name || 'Loading…'}</span>
            </div>
            <span className="text-[10px] text-slate-400 font-sans">Company Info</span>
          </div>

          {loading ? (
            <div className="py-2 text-center text-slate-400 font-sans flex items-center justify-center gap-1.5">
              <span className="inline-block animate-spin rounded-full h-3 w-3 border-2 border-brand-400 border-t-transparent" />
              Loading company details…
            </div>
          ) : company ? (
            <div className="space-y-1.5 font-sans">
              <div>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Company Name</span>
                <p className="font-semibold text-slate-100">{company.company_name || '—'}</p>
              </div>

              {company.occupier_name && company.occupier_name !== 'N/A' && (
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Occupier</span>
                  <p className="text-slate-200">{company.occupier_name}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 text-[11px] pt-0.5">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase block">License No.</span>
                  <p className="font-mono text-slate-300">{company.license_number || '—'}</p>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase block">Reg No.</span>
                  <p className="font-mono text-slate-300">{company.registration_number || '—'}</p>
                </div>
              </div>

              {company.contact_email && (
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Contact Email</span>
                  <p className="text-brand-300 font-mono text-[11px] truncate">{company.contact_email}</p>
                </div>
              )}

              {company.communication_email && (
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider block">Communication Email</span>
                  <p className="text-brand-300 font-mono text-[11px] truncate">{company.communication_email}</p>
                </div>
              )}

              {company.factory_address && company.factory_address !== 'N/A' && (
                <div>
                  <span className="text-[10px] text-slate-400 uppercase block">Factory Location</span>
                  <p className="text-[11px] text-slate-300 line-clamp-2 leading-tight">{company.factory_address}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-400 text-center py-1 font-sans">Company details unavailable</p>
          )}
        </div>
      )}
    </span>
  );
}
